import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, IconButton, Collapse, ToggleButtonGroup, ToggleButton, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as AbrirIcon,
  KeyboardArrowUp as CerrarIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Intentos de compra desde la app.
//
// El portal solo sabía de las compras que salían bien, por el webhook de RevenueCat. Quien
// quería pagar y no pudo no dejaba rastro. Este panel es ese rastro: quién tocó "comprar", en
// qué terminó, y si la tienda llegó siquiera a mostrarle los planes.
// ─────────────────────────────────────────────────────────────────────────────

type Resultado = 'compro' | 'fallo' | 'cancelo' | 'sin_resultado' | 'solo_problema';

interface EventoCompra {
  event: string;
  receivedAt: string;
  plan?: string | null;
  interval?: string | null;
  error?: string | null;
  errorType?: string | null;
  autenticado?: boolean;
}

interface UsuarioCompra {
  userId: string;
  nombre?: string | null;
  email?: string | null;
  suscripcion?: { status?: string; plan?: string } | null;
  autenticado: boolean;
  intentos: number;
  ultimoPlan?: string | null;
  ultimoIntervalo?: string | null;
  resultado: Resultado;
  ultimoError?: string | null;
  ultimaVez: string;
  eventos: EventoCompra[];
}

interface Resumen {
  usuarios: number;
  intentos: number;
  compras: number;
  fallos: number;
  cancelados: number;
  sinResultado: number;
  sinPlanes: number;
  arranques: number;
  arranquesFallidos: number;
  tasaFalloArranque: number | null;
}

const RESULTADO: Record<Resultado, { label: string; color: 'success' | 'error' | 'default' | 'warning'; ayuda: string }> = {
  compro: { label: 'Compró', color: 'success', ayuda: 'La tienda cobró: llegó el aviso de RevenueCat o la app lo confirmó.' },
  fallo: { label: 'Falló', color: 'error', ayuda: 'La app reportó un error al intentar pagar.' },
  cancelo: { label: 'Canceló', color: 'default', ayuda: 'La persona cerró la pantalla de pago de la tienda.' },
  sin_resultado: { label: 'Sin resultado', color: 'warning', ayuda: 'Tocó comprar y no llegó ni la compra ni un error. Puede haberse quedado a medias.' },
  solo_problema: { label: 'No vio los planes', color: 'error', ayuda: 'La tienda no arrancó o no cargó los planes, así que ni llegó a poder comprar.' },
};

const EVENTO: Record<string, string> = {
  rc_init_start: 'Arrancó la tienda',
  rc_init_success: 'Tienda lista',
  rc_init_failed: 'La tienda no arrancó',
  rc_packages_success: 'Planes cargados',
  rc_packages_empty: 'La tienda no devolvió planes',
  rc_packages_error: 'Error cargando los planes',
  purchase_attempt: 'Tocó comprar',
  purchase_success: 'Compra confirmada',
  purchase_failed: 'La compra falló',
  purchase_cancelled: 'Canceló el pago',
};

const INTERVALO: Record<string, string> = { month: 'mensual', year: 'anual', week: 'semanal' };

const cuando = (iso: string) =>
  new Date(iso).toLocaleString('es-DO', {
    timeZone: 'America/Santo_Domingo', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });

const FilaUsuario: React.FC<{ u: UsuarioCompra }> = ({ u }) => {
  const [abierta, setAbierta] = useState(false);
  const r = RESULTADO[u.resultado];
  // Eventos de antes del login: la tienda arranca al abrir la app, cuando aún no hay usuario.
  const sinSesion = u.userId === 'sin-usuario';
  return (
    <>
      <TableRow hover sx={{ '& > td': { borderBottom: abierta ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setAbierta(!abierta)} aria-label={abierta ? 'Ocultar eventos' : 'Ver eventos'}>
            {abierta ? <CerrarIcon /> : <AbrirIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {sinSesion ? 'Sin sesión iniciada' : (u.nombre || u.email || `Usuario ${u.userId}`)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sinSesion
              ? 'eventos de antes de entrar a la app'
              : [u.nombre ? u.email : null, `Traccar ${u.userId}`].filter(Boolean).join(' · ') + (u.autenticado ? '' : ' · sin verificar')}
          </Typography>
        </TableCell>
        <TableCell>
          {u.ultimoPlan
            ? `${u.ultimoPlan}${u.ultimoIntervalo ? ` · ${INTERVALO[u.ultimoIntervalo] || u.ultimoIntervalo}` : ''}`
            : '—'}
          {u.suscripcion?.status && (
            <Typography variant="caption" color="text.secondary" display="block">
              hoy: {u.suscripcion.plan || 'sin plan'} ({u.suscripcion.status})
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">{u.intentos}</TableCell>
        <TableCell>
          <Tooltip title={r.ayuda}><Chip label={r.label} color={r.color} size="small" /></Tooltip>
        </TableCell>
        <TableCell sx={{ maxWidth: 280 }}>
          <Typography variant="body2" color={u.ultimoError ? 'error' : 'text.secondary'} sx={{ wordBreak: 'break-word' }}>
            {u.ultimoError || '—'}
          </Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{cuando(u.ultimaVez)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={abierta} unmountOnExit>
            <Box sx={{ py: 1.5, pl: 7 }}>
              <Typography variant="caption" color="text.secondary">Últimos eventos, del más reciente al más viejo</Typography>
              <Table size="small">
                <TableBody>
                  {u.eventos.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 150 }}>{cuando(e.receivedAt)}</TableCell>
                      <TableCell>{EVENTO[e.event] || e.event}</TableCell>
                      <TableCell>{e.plan ? `${e.plan}${e.interval ? ` · ${INTERVALO[e.interval] || e.interval}` : ''}` : ''}</TableCell>
                      <TableCell sx={{ color: 'error.main', wordBreak: 'break-word' }}>
                        {[e.errorType, e.error].filter(Boolean).join(': ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const PurchaseAttemptsPanel: React.FC = () => {
  const [dias, setDias] = useState(7);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [usuarios, setUsuarios] = useState<UsuarioCompra[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await apiService.getPurchaseAttempts(dias);
      setResumen(res.data?.resumen || null);
      setUsuarios(res.data?.usuarios || []);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Intentos de compra desde la app</Typography>
            <Typography variant="body2" color="text.secondary">
              Quién quiso pagar un plan, en qué terminó y dónde se trabó.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup size="small" exclusive value={dias} onChange={(_, v) => v && setDias(v)}>
              <ToggleButton value={1}>Hoy</ToggleButton>
              <ToggleButton value={7}>7 días</ToggleButton>
              <ToggleButton value={30}>30 días</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Actualizar">
              <IconButton onClick={cargar} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>No se pudieron cargar los intentos: {error}</Alert>}

        {resumen && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`${resumen.usuarios} persona${resumen.usuarios === 1 ? '' : 's'} · ${resumen.intentos} intento${resumen.intentos === 1 ? '' : 's'}`} />
            <Chip color="success" variant="outlined" label={`${resumen.compras} compraron`} />
            <Chip color="error" variant="outlined" label={`${resumen.fallos} fallaron`} />
            <Chip color="warning" variant="outlined" label={`${resumen.sinResultado} sin resultado`} />
            {resumen.sinPlanes > 0 && <Chip color="error" variant="outlined" label={`${resumen.sinPlanes} no vieron los planes`} />}
            {resumen.cancelados > 0 && <Chip variant="outlined" label={`${resumen.cancelados} cancelaron`} />}
            <Tooltip title="Cada vez que alguien abre la app, la tienda arranca. Esto dice cuántas veces no pudo.">
              <Chip
                variant="outlined"
                color={resumen.arranquesFallidos > 0 ? 'error' : 'default'}
                label={resumen.tasaFalloArranque == null
                  ? 'Tienda: sin arranques reportados'
                  : `Tienda: ${resumen.arranquesFallidos} de ${resumen.arranques} arranques fallaron (${resumen.tasaFalloArranque}%)`}
              />
            </Tooltip>
          </Box>
        )}

        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : usuarios.length === 0 && !error ? (
          <Alert severity="info">
            Nadie ha intentado comprar desde la app en {dias === 1 ? 'el día de hoy' : `los últimos ${dias} días`}.
            Si esperabas ver datos, revisa que la versión de la app que envía estos eventos ya esté publicada.
          </Alert>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Persona</TableCell>
                  <TableCell>Plan que intentó</TableCell>
                  <TableCell align="center">Intentos</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell>Último error</TableCell>
                  <TableCell>Última vez</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usuarios.map((u) => <FilaUsuario key={u.userId} u={u} />)}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default PurchaseAttemptsPanel;
